import { useCallback, useEffect, useMemo, useState } from 'react'

import { THEME_ATTRIBUTE, THEME_STORAGE_KEY } from '../../shared/security/theme-bootstrap.ts'

export type Theme = 'auto' | 'claro' | 'escuro'
export type ResolvedTheme = 'claro' | 'escuro'

export interface ThemeState {
  readonly resolved: ResolvedTheme
  readonly toggle: () => void
}

export const DARK_COLOR_SCHEME_QUERY = '(prefers-color-scheme: dark)'

/**
 * What the server renders and what the client renders on its very first pass.
 *
 * A statically served document cannot know the reader's theme at build time, so
 * this is the only honest option: render light, then correct one frame later from
 * the effect. Guessing would produce a wrong first frame just as often.
 */
const SERVER_RESOLVED: ResolvedTheme = 'claro'

function prefersDark(): boolean {
  return window.matchMedia(DARK_COLOR_SCHEME_QUERY).matches
}

/**
 * The current theme as the cascade sees it: an explicit `data-tema` if one is
 * stamped, otherwise whatever the system asks for.
 *
 * Exported because the sky feature needs exactly this answer too. Two copies of
 * this logic would diverge the first time either one changed.
 *
 * Client-only — touches `document` and `matchMedia`. Never call during render.
 */
export function resolveTheme(): ResolvedTheme {
  const attribute = document.documentElement.getAttribute(THEME_ATTRIBUTE)

  if (attribute === 'claro' || attribute === 'escuro') {
    return attribute
  }

  return prefersDark() ? 'escuro' : 'claro'
}

/**
 * `localStorage` throws outright in Safari private mode and under some
 * cookie-blocking configurations — not returns null, throws. The Lighthouse gate
 * is `errors-in-console: maxLength 0`, so neither of these may escape.
 */
function readStoredTheme(): ResolvedTheme | undefined {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY)
    return stored === 'claro' || stored === 'escuro' ? stored : undefined
  } catch {
    // Storage unavailable. The reader simply does not get persistence.
    return undefined
  }
}

function writeStoredTheme(theme: ResolvedTheme): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme)
  } catch {
    // As above — a failed write costs persistence, never the interaction.
  }
}

/**
 * Theme selection: three states, persisted, no flash of the wrong theme.
 *
 * Initial state is the same on server and client, so the hydrated tree matches
 * the prerendered HTML byte for byte. The pre-paint bootstrap in
 * `theme-bootstrap.ts` has already stamped `data-tema` by the time this mounts,
 * so the effect's job is to read that back into React state, not to race it.
 *
 * The effect here is legitimate: it synchronises with two external systems
 * (storage and the OS colour-scheme preference). It is not deriving state from
 * props, which rules/frontend.md § 2 forbids.
 *
 * Once toggled, the theme is explicit; there is no path back to `auto` from this
 * button. That matches the design. A third state needs a three-way control, not a
 * longer press.
 */
export function useTheme(): ThemeState {
  const [resolved, setResolved] = useState<ResolvedTheme>(SERVER_RESOLVED)

  useEffect(() => {
    const stored = readStoredTheme()
    if (stored !== undefined) {
      document.documentElement.setAttribute(THEME_ATTRIBUTE, stored)
    }

    setResolved(resolveTheme())

    const query = window.matchMedia(DARK_COLOR_SCHEME_QUERY)
    const handleChange = (): void => {
      setResolved(resolveTheme())
    }

    query.addEventListener('change', handleChange)
    return () => {
      query.removeEventListener('change', handleChange)
    }
  }, [])

  const toggle = useCallback(() => {
    // Flip against what is actually rendered, not against stale state: the
    // attribute is the contract the cascade in _tokens.sass is built on.
    const next: ResolvedTheme = resolveTheme() === 'escuro' ? 'claro' : 'escuro'

    document.documentElement.setAttribute(THEME_ATTRIBUTE, next)
    writeStoredTheme(next)
    setResolved(next)
  }, [])

  return useMemo(() => ({ resolved, toggle }), [resolved, toggle])
}
